import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import type {
  GroupSubscriptionBatch,
  GroupSubscriptionBatchDetail,
  LessonPaymentMethod,
  LessonSubscriptionGrantDistribution,
  LessonSubscriptionPlan,
} from '@/types/billing'
import { formatMoney } from './formatMoney'
import { getErrorMessage } from '@/utils/error'

type Props = {
  groupId: number
}

const ISSUE_METHODS: LessonPaymentMethod[] = ['from_balance', 'cash', 'deferred']

export function GroupFinanceSection({ groupId }: Props) {
  const { t } = useTranslation()
  const [batches, setBatches] = useState<GroupSubscriptionBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [issueOpen, setIssueOpen] = useState(false)
  const [issueLoading, setIssueLoading] = useState(false)
  const [issueLoadError, setIssueLoadError] = useState<string | null>(null)
  const [plans, setPlans] = useState<LessonSubscriptionPlan[]>([])
  const [issuePlanId, setIssuePlanId] = useState<number | ''>('')
  const [issueStartsAt, setIssueStartsAt] = useState('')
  const [issueMethod, setIssueMethod] = useState<LessonPaymentMethod>('from_balance')
  const [submitting, setSubmitting] = useState(false)
  const [batchDetail, setBatchDetail] = useState<GroupSubscriptionBatchDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailLoadError, setDetailLoadError] = useState<string | null>(null)
  const [loadingBatchId, setLoadingBatchId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await lessonSubscriptionsApi.listForGroup(groupId)
      setBatches(data.batches ?? [])
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [groupId, t])

  useEffect(() => {
    load().catch(() => {})
  }, [load])

  const openIssue = async () => {
    setIssueOpen(true)
    setIssueLoading(true)
    setIssueLoadError(null)
    setPlans([])
    setIssuePlanId('')
    try {
      const res = await lessonSubscriptionsApi.listPlans({
        is_active: true,
        subscription_type: 'group',
      })
      setPlans(res.data)
      setIssuePlanId(res.data[0]?.id ?? '')
      setIssueStartsAt(new Date().toISOString().slice(0, 10))
    } catch (e: unknown) {
      setIssueLoadError(getErrorMessage(e) || t('common.error'))
    } finally {
      setIssueLoading(false)
    }
  }

  const closeIssueDialog = () => {
    if (submitting) return
    setIssueOpen(false)
    setIssueLoading(false)
    setIssueLoadError(null)
  }

  const handleIssue = async () => {
    if (!issuePlanId || !issueStartsAt) return
    setSubmitting(true)
    try {
      await lessonSubscriptionsApi.issueForGroup(groupId, {
        plan_id: Number(issuePlanId),
        starts_at: issueStartsAt,
        payment_method: issueMethod,
      })
      setIssueOpen(false)
      await load()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const openBatchDetail = async (batchId: string) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailLoadError(null)
    setBatchDetail(null)
    setLoadingBatchId(batchId)
    try {
      const detail = await lessonSubscriptionsApi.getGroupBatch(groupId, batchId)
      setBatchDetail(detail)
    } catch (e: unknown) {
      setDetailLoadError(getErrorMessage(e) || t('common.error'))
    } finally {
      setDetailLoading(false)
      setLoadingBatchId(null)
    }
  }

  const closeDetailDialog = () => {
    setDetailOpen(false)
    setDetailLoading(false)
    setDetailLoadError(null)
    setBatchDetail(null)
    setLoadingBatchId(null)
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            {t('billing.financeSection', 'Финансы')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => void openIssue()}
            disabled={issueLoading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('billing.issueGroupSubscription', 'Выдать групповой абонемент')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Paper>
            <Skeleton variant="rectangular" width="100%" height={120} />
          </Paper>
        ) : batches.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('billing.noGroupSubscriptions', 'Групповые абонементы не выдавались')}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.plan', 'Абонемент')}</TableCell>
                  <TableCell>{t('billing.startsAt', 'Начало')}</TableCell>
                  <TableCell>{t('billing.participants', 'Участники')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.batch_id}>
                    <TableCell>{batch.plan?.title ?? '—'}</TableCell>
                    <TableCell>{batch.starts_at ?? '—'}</TableCell>
                    <TableCell>{batch.participants_count}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={loadingBatchId === batch.batch_id}
                        onClick={() => void openBatchDetail(batch.batch_id)}
                      >
                        {t('billing.distribution', 'Распределение')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={issueOpen} onClose={closeIssueDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.issueGroupSubscription', 'Выдать групповой абонемент')}</DialogTitle>
        <DialogContent>
          {issueLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Box>
          ) : issueLoadError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {issueLoadError}
            </Alert>
          ) : plans.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('billing.noGroupPlans', 'Нет доступных групповых абонементов')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('billing.plan', 'Абонемент')}</InputLabel>
                <Select
                  label={t('billing.plan', 'Абонемент')}
                  value={issuePlanId}
                  onChange={(e) => setIssuePlanId(Number(e.target.value))}
                >
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label={t('billing.startsAt', 'Начало')}
                InputLabelProps={{ shrink: true }}
                value={issueStartsAt}
                onChange={(e) => setIssueStartsAt(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{t('billing.paymentMethod', 'Способ оплаты')}</InputLabel>
                <Select
                  label={t('billing.paymentMethod', 'Способ оплаты')}
                  value={issueMethod}
                  onChange={(e) => setIssueMethod(e.target.value as LessonPaymentMethod)}
                >
                  {ISSUE_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {t(`billing.paymentMethodOption.${m}`, m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIssueDialog} disabled={submitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button
            variant="contained"
            disabled={submitting || issueLoading || !!issueLoadError || plans.length === 0}
            onClick={() => void handleIssue()}
          >
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={closeDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t('billing.distributionTitle', 'Распределение абонемента')}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
            </Box>
          ) : detailLoadError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {detailLoadError}
            </Alert>
          ) : batchDetail ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.student', 'Ученик')}</TableCell>
                  <TableCell>{t('billing.shareAmount', 'Доля')}</TableCell>
                  <TableCell>{t('billing.lessonsRemainingColumn', 'Остаток')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchDetail.distributions.map((d: LessonSubscriptionGrantDistribution) => {
                  const student = d.grant?.contract?.student
                  const name =
                    student?.display_name ||
                    [student?.surname, student?.first_name].filter(Boolean).join(' ') ||
                    student?.email ||
                    `#${d.contract_id}`
                  const remaining = d.grant?.lessons_remaining
                  const total = d.grant?.lessons_total
                  return (
                    <TableRow key={d.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{formatMoney(d.share_amount)}</TableCell>
                      <TableCell>
                        {remaining != null && total != null
                          ? t('billing.lessonsRemaining', '{{remaining}} из {{total}}', {
                              remaining,
                              total,
                            })
                          : '—'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>{t('common.close', 'Закрыть')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
